import { reactive } from './reactive'
import { unsafeEffect } from './lifecycle'
import { hook, getComponent, setGlobalKeyReleaseHandler } from './hook'
import { registerSignal, updateSignal } from './devtools'
import { Context } from './context'
import { Useable, isUseable } from './useable'
import { REACTIVITY_V2 } from './feature-flags'
import { createSignalV2, makeSignalContainer, trackV2, triggerV2 } from './reactive-v2'
import { scheduleEffect } from './scheduler'

// Re-export Context and Useable
export { Context } from './context'
export { Useable, isUseable } from './useable'

// Types
export type Setter<T> = (newValue: T | ((prev: T) => T)) => void

export type ResourceControl<P = void> = {
  refetch: (params?: P) => Promise<void>
  readonly loading: boolean
  readonly error: unknown
  readonly status: 'idle' | 'loading' | 'success' | 'error'
}

export interface UseContext<P = void> {
  onCleanup: (fn: () => void) => void
  params?: P
}

export interface UseOptions {
  key?: unknown[]
  name?: string
}

// Phase 6C: plain-object stateRef shape — replaces reactive({}) Proxy allocation.
// stateRef is purely internal bookkeeping across renders; it never needs reactivity
// (no effect or computed reads its fields — verified Task 11 grep). V8 can
// monomorphize all stateRef reads/writes since every instance has this exact shape.
interface StateRef {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime container, type narrowed below via .type discriminant
  container: any
  serializedKey: string | undefined
}

// Global State Registry

interface RegistryEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic runtime containers require any for heterogeneous signal/resource storage
  container: any
  refCount: number
}

const globalRegistry = new Map<string, RegistryEntry>()

function releaseGlobalKeyImpl(key: string): void {
  const entry = globalRegistry.get(key)
  if (!entry) return
  entry.refCount--
  if (entry.refCount <= 0) {
    globalRegistry.delete(key)
  }
}

// Register the release handler with hook.ts (avoids circular import — hook.ts
// never imports from use.ts; use.ts calls this at module-init time).
setGlobalKeyReleaseHandler(releaseGlobalKeyImpl)

/**
 * Force-evict a globally-keyed signal from the registry.
 *
 * Normally registry entries are reference-counted: each component reading a global
 * key increments refCount on mount and decrements on unmount. The entry is auto-evicted
 * when refCount reaches 0.
 *
 * Use this only when you need to explicitly clear cached state (e.g., invalidating a
 * cache, logout flow). Active subscribers will be left with a dangling reference if
 * you force-evict — call this only when you're sure nothing reads the key anymore.
 *
 * @example
 * releaseGlobalKey(JSON.stringify(['user', userId]))
 */
export const releaseGlobalKey = releaseGlobalKeyImpl

function serializeKey(key: unknown[]): string {
  return JSON.stringify(key)
}

// ---------------------------------------------------------------------------
// Module-scope factories — eliminate per-call closure allocation. (Phase 5D)
// ---------------------------------------------------------------------------

/**
 * Shape of a resource/computed container that the public ResourceControl
 * exposes via accessors. `loading`, `error`, `status` are read every render;
 * `run` is invoked on `.refetch()`.
 */
interface ResourceContainerLike<P> {
  loading: boolean
  error: unknown
  status: string
  run: (params?: P) => void
}

/**
 * @internal — module-scope factory used by every use(fn,...) path. Returns a
 * fresh ResourceControl wrapper whose accessors delegate to the underlying
 * container by closure. The accessor functions are NOT shared across calls,
 * but moving the body here keeps use() smaller and lets V8 share the IC for
 * this construction site across all components.
 */
function makeResourceControl<P>(
  container: ResourceContainerLike<P>
): ResourceControl<P> {
  return {
    refetch: async (params?: P) => { container.run(params) },
    get loading() { return container.loading },
    get error() { return container.error },
    get status() { return container.status as 'idle' | 'loading' | 'success' | 'error' },
  }
}

/** No-op container for the sync deps-mode path (loading=false, status='success'). */
const NOOP_RESOURCE_CONTAINER: ResourceContainerLike<unknown> = {
  loading: false,
  error: null,
  status: 'success',
  run: () => {},
}

// Overloads

/**
 * Consume a Context value provided by an ancestor Provider.
 *
 * @example
 * const [theme] = use(ThemeContext);
 *
 * @param ctx - The Context object created with `createContext`
 * @returns Tuple of [contextValue, undefined]
 */
export function use<T>(ctx: Context<T>): [T, undefined]

/**
 * Subscribe to a Useable data source (Stream, Shared, or any custom Useable).
 *
 * @example
 * const [messages, send] = use(chatStream, { roomId });
 *
 * @param source - A Useable object (e.g. Stream, Shared)
 * @param params - Optional parameters forwarded to the Useable's subscribe call
 * @returns Tuple of [currentValue, ...actions] where actions come from the Useable
 */
export function use<T, P, A extends unknown[]>(source: Useable<T, P, A>, params?: P): [T, ...A]

/**
 * Create an async resource that fetches data and tracks loading/error state.
 *
 * @example
 * const [user, ctrl] = use(async () => fetchUser(id));
 * if (ctrl.loading) return html`<spinner />`;
 * ctrl.refetch();
 *
 * @param fn - Async function (optionally using `ctx.onCleanup`) that returns a Promise
 * @param depsOrOptions - Dependency array or UseOptions (key, name)
 * @param options - UseOptions when `depsOrOptions` is a deps array
 * @returns Tuple of [resolvedValue | undefined, ResourceControl]
 */
export function use<T, P = void>(
  fn: (ctx: UseContext<P>) => Promise<T>,
  depsOrOptions?: unknown[] | UseOptions,
  options?: UseOptions
): [T | undefined, ResourceControl<P>]

/**
 * Create a computed (derived) value that re-runs when its deps change.
 *
 * @example
 * const [doubled, ctrl] = use((ctx) => count() * 2, [count]);
 *
 * @param fn - Sync computation function; receives a UseContext for cleanup registration
 * @param depsOrOptions - Dependency array that controls when `fn` re-runs, or UseOptions
 * @param options - UseOptions when `depsOrOptions` is a deps array
 * @returns Tuple of [computedValue, ResourceControl]
 */
export function use<T>(
  fn: (ctx: UseContext) => T,
  depsOrOptions?: unknown[] | UseOptions,
  options?: UseOptions
): [T, ResourceControl]

/**
 * Create a local reactive signal with an initial value.
 *
 * @example
 * const [count, setCount] = use(0);
 * setCount(c => c + 1);
 *
 * @param initialValue - Initial value of the signal (must not be a function)
 * @param options - Optional UseOptions (key for global registry, name for DevTools)
 * @returns Tuple of [currentValue, setter]
 */
export function use<T>(
  initialValue: T extends (...args: unknown[]) => unknown ? never : T,
  options?: UseOptions
): [T, Setter<T>]

export function use<T, P = void>(
  input: T | Useable<T, P> | ((ctx: UseContext<P>) => T) | ((ctx: UseContext<P>) => Promise<T>),
  depsOrOptions?: unknown[] | UseOptions | P,
  thirdArg?: UseOptions
): unknown {
  // Useable mode: use(SomeUseable, params?) returns [value, undefined] or [value, send] for sendable
  // This handles Context, Stream, Shared, and any custom Useable
  if (isUseable(input)) {
    const source = input as Useable<T, P>
    const params = depsOrOptions as P | undefined

    // Hook to store subscription state
    const state = hook(() => {
      if (REACTIVITY_V2) {
        // V2: backing signal container + non-reactive cleanup slot.
        const v2Container = makeSignalContainer<T>(source.getInitial(params))
        const hybrid = {
          _v2: true as const,
          _v2Container: v2Container,
          cleanup: undefined as (() => void) | undefined,
          get value(): T { trackV2(v2Container); return v2Container.value },
          set value(v: T) {
            if (!Object.is(v, v2Container.value)) {
              v2Container.value = v
              triggerV2(v2Container, scheduleEffect)
            }
          },
        }

        // Subscribe to updates — writes go through hybrid.value setter
        hybrid.cleanup = source.subscribe(params, (newValue) => {
          hybrid.value = newValue
        })

        return hybrid
      } else {
        const s = reactive({
          value: source.getInitial(params),
          cleanup: undefined as (() => void) | undefined
        })

        // Subscribe to updates
        s.cleanup = source.subscribe(params, (newValue) => {
          s.value = newValue
        })

        return s
      }
    })

    // Access value to track dependency
    const currentValue = state.value

    // Get additional actions from the source (if any)
    const actions = source.getActions()
    if (actions && actions.length > 0) {
      return [currentValue, ...actions]
    }

    return [currentValue, undefined]
  }

  // Normalize arguments:
  // - use(value, { key }) → options only
  // - use(fn, [deps]) → deps only
  // - use(fn, [deps], { key }) → deps + options
  let deps: unknown[] | undefined
  let options: UseOptions | undefined

  if (Array.isArray(depsOrOptions)) {
    deps = depsOrOptions
    options = thirdArg  // third arg is options when second is deps array
  } else if (depsOrOptions && typeof depsOrOptions === 'object') {
    options = depsOrOptions as UseOptions
  }

  // Validate key if provided
  if (options?.key && !Array.isArray(options.key)) {
    throw new Error('State key must be an array')
  }

  // Phase 6B: resolve current component once; reuse for inlined stateRef
  // bookkeeping + the globalKeys tracking calls below. Eliminates 2-3 redundant
  // getComponent() calls per use() invocation in the signal path.
  const _component = getComponent()

  // Phase 6B: inline hook() bookkeeping for the stateRef slot.
  // Avoids hook() function call + arrow-function allocation on every render.
  // Phase 6C: stateRef is a plain object — no reactive() Proxy. V8 can
  // monomorphize all stateRef reads/writes since the shape is fixed at
  // construction (container + serializedKey, both initialized to undefined).
  let stateRef: StateRef
  if (_component) {
    // DEV hook-order signature — push on EVERY render (matches hook()'s
    // semantics: hookSignatures must contain one entry per hook call so
    // checkHookOrder's prev.length === curr.length invariant holds).
    if (import.meta.env.DEV) {
      _component.hookSignatures!.push('stateRef')
    }
    const _hi = _component.hookIndex
    if (_hi < _component.hooks.length) {
      // Re-render: return cached stateRef without running factory
      stateRef = _component.hooks[_hi] as StateRef
      _component.hookIndex = _hi + 1
    } else {
      // First render: create and store. Plain object literal — V8-friendly
      // stable shape, no Proxy allocation.
      stateRef = { container: undefined, serializedKey: undefined }
      _component.hooks.push(stateRef)
      _component.hookIndex = _hi + 1
    }
  } else {
    // Outside component context — create ephemeral ref (no caching).
    stateRef = { container: undefined, serializedKey: undefined }
  }

  // Compute serialized key
  const currentKey = options?.key
  const serializedKey = currentKey ? serializeKey(currentKey) : undefined

  // Check if key has changed
  const keyChanged = serializedKey !== stateRef.serializedKey

  // DEPS MODE: Function with explicit deps array
  if (typeof input === 'function' && deps !== undefined) {
    const fn = input as (ctx: UseContext<P>) => T | Promise<T>

    const memoState = hook(() => ({
      value: undefined as T | undefined,
      prevDeps: undefined as unknown[] | undefined,
      cleanup: undefined as (() => void) | undefined,
      hasRun: false,
      params: undefined as P | undefined
    }))

    let hasChanged = true
    if (memoState.hasRun && memoState.prevDeps) {
      hasChanged = deps.length !== memoState.prevDeps.length ||
        deps.some((d, i) => d !== memoState.prevDeps![i])
    }

    if (hasChanged) {
      // Run previous cleanup
      if (memoState.cleanup) {
        memoState.cleanup()
        memoState.cleanup = undefined
      }

      // Create context with onCleanup
      const ctx: UseContext<P> = {
        onCleanup: (fn) => {
          memoState.cleanup = fn
        },
        params: memoState.params
      }

      const result = fn(ctx)

      if (result instanceof Promise) {
        throw new Error('deps with async functions is not supported. Use use(asyncFn) without deps for async resources.')
      }

      memoState.value = result
      memoState.prevDeps = [...deps]
      memoState.hasRun = true
    }

    const control = makeResourceControl<P>(NOOP_RESOURCE_CONTAINER as ResourceContainerLike<P>)

    return [memoState.value, control]
  }

  // If key changed or first time, get/create container
  if (!stateRef.container || keyChanged) {
    stateRef.serializedKey = serializedKey

    // Check Registry FIRST
    if (serializedKey && globalRegistry.has(serializedKey)) {
      const entry = globalRegistry.get(serializedKey)!
      entry.refCount++
      stateRef.container = entry.container
      // Track this key on the component for later release.
      // Reuse Phase 6B hoisted _component — no second getComponent() call.
      if (_component) {
        if (!_component.globalKeys) _component.globalKeys = []
        _component.globalKeys.push(serializedKey)
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime container, type narrowed below via .type discriminant
      let newContainer: any

      // Function (Computed or Resource)
      if (typeof input === 'function') {
        const fn = input as (ctx: UseContext<P>) => T | Promise<T>

        // State for async/computed
        const state = reactive({
          type: 'resource',
          value: undefined as T | undefined,
          loading: true,
          error: null as unknown,
          status: 'idle' as 'idle' | 'loading' | 'success' | 'error',
          cleanup: undefined as (() => void) | undefined,
          params: undefined as P | undefined,
          run: () => {}
        })

        const run = (params?: P) => {
          // Run previous cleanup
          if (state.cleanup) {
            state.cleanup()
            state.cleanup = undefined
          }

          // Store params for context
          state.params = params

          try {
            // Create context
            const ctx: UseContext<P> = {
              onCleanup: (fn) => {
                state.cleanup = fn
              },
              params: state.params
            }

            const result = fn(ctx)

            if (result instanceof Promise) {
              state.loading = true
              state.status = 'loading'
              state.error = null

              result
                .then(data => {
                  state.value = data
                  state.status = 'success'
                  state.loading = false
                })
                .catch(err => {
                  state.error = err
                  state.status = 'error'
                  state.loading = false
                })
            } else {
              state.value = result
              state.status = 'success'
              state.loading = false
              state.error = null
            }
          } catch (err) {
            state.error = err
            state.status = 'error'
            state.loading = false
          }
        }

        state.run = run

        // Make it reactive! Plain unsafeEffect (not .detached) so the effect is
        // auto-registered with the enclosing component and stopped on unmount,
        // preventing the async resource from leaking reactive subscriptions.
        unsafeEffect(() => run())

        newContainer = state
      } else {
        // Value (Signal)
        if (REACTIVITY_V2) {
          // V2: createSignalV2 returns a SignalContainerV2 with the same shape
          // as the former `hybrid` object literal — single allocation, stable
          // hidden class, prototype-level value accessor. (Phase 5B)
          newContainer = createSignalV2(input, scheduleEffect)
        } else {
          newContainer = reactive({
            type: 'signal',
            value: input
          })
        }

        // Register with DevTools (DEV-only — folded away in production builds)
        if (import.meta.env.DEV) {
          registerSignal(newContainer, options?.name)
        }
      }

      // Register in global registry if needed
      if (serializedKey) {
        const newEntry: RegistryEntry = { container: newContainer, refCount: 1 }
        globalRegistry.set(serializedKey, newEntry)
        // Track this key on the component for later release.
        // Reuse Phase 6B hoisted _component — no second getComponent() call.
        if (_component) {
          if (!_component.globalKeys) _component.globalKeys = []
          _component.globalKeys.push(serializedKey)
        }
      }

      stateRef.container = newContainer
    }
  }

  const container = stateRef.container

  // Access container.value to track dependency
  const currentValue = container.value

  if (container.type === 'signal') {
    // Phase 4: use a named function expression rather than an arrow. The name
    // gives V8 a stable identity for inline-cache specialization across the
    // many setter invocations during a component's lifetime.
    const setter: Setter<T> = function signalSetter(newValue) {
      if (container._v2 && container._setter) {
        // V2 setter: handles functional updates + Object.is dedup + trigger internally
        container._setter(newValue)
      } else {
        if (typeof newValue === 'function') {
          container.value = (newValue as (prev: T) => T)(container.value)
        } else {
          container.value = newValue
        }
      }
      if (import.meta.env.DEV) {
        updateSignal(container, container.value)
      }
    }
    return [currentValue, setter]
  } else {
    // Resource / Computed
    const control = makeResourceControl<P>(container as ResourceContainerLike<P>)
    return [currentValue, control]
  }
}

// ---------------------------------------------------------------------------
// Test helpers (not intended for production use)
// ---------------------------------------------------------------------------

/** @internal — for tests only */
export function __registrySize(): number {
  return globalRegistry.size
}

/** @internal — for tests only */
export function __registryHas(key: string): boolean {
  return globalRegistry.has(key)
}
